import React, { useState, useRef, useCallback } from 'react';
import {
  Paper, Tabs, Tab, Box, Typography, IconButton, Divider, List, ListItem, ListItemText
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';

const PANEL_WIDTH = 460;
const PANEL_HEIGHT = 520;
const MIN_WIDTH = 300;
const MIN_HEIGHT = 260;

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <Box sx={{ p: 2, overflowY: 'auto', flex: 1, fontSize: '0.875rem' }}>
      {children}
    </Box>
  );
}

// --- Tab 1: Dance Styles ---
function DanceStylesTab() {
  const competitive = [
    'Ballet', 'Contemporary', 'Cultural (creative choreography)', 'Fusion', 'Hip Hop', 'Jazz',
    'K-Pop - Feedback and technical score only',
    'Lyrical', 'Modern', 'Musical Theatre (Lip Sync)', 'Musical Theatre (Live Vocals)',
    'Open', 'Tap',
  ];
  const nonCompetitive = ['Creative Collaboration - Feedback only',
    'Cultural (Set Choreography) - Feedback and Technical score only',
    'Freestyle (Hip Hop and Contemporary) - Feedback, no score, select top 5 with other judges', 
    'Teacher Choreography - score as usual'];
  return (
    <>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Competitive</Typography>
      <List dense disablePadding sx={{ mb: 2 }}>
        {competitive.map(s => (
          <ListItem key={s} disableGutters sx={{ py: 0.1 }}>
            <ListItemText primary={s} primaryTypographyProps={{ fontSize: '0.85rem' }} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Non-Competitive - not included in Overall Awards</Typography>
      <List dense disablePadding>
        {nonCompetitive.map(s => (
          <ListItem key={s} disableGutters sx={{ py: 0.1 }}>
            <ListItemText primary={s} primaryTypographyProps={{ fontSize: '0.85rem' }} />
          </ListItem>
        ))}
      </List>
    </>
  );
}

// --- Tab 2: Group Sizes & Time Limits ---
function GroupSizesTab() {
  const limits = [
    { group: 'Solos, Duets, and Trios', time: '2:30 max' },
    { group: 'Small Group (4–9 Dancers)', time: '3:00 max' },
    { group: 'Medium Group (10–15 Dancers)', time: '3:30 max' },
    { group: 'Large Group (16+ Dancers)', time: '4:00 max' },
    { group: 'Production', time: '5:00 max (limit one per school)' },
    { group: 'Creative Collaboration', time: '5:00 max (limit one per school)' },
  ];
  return (
    <>
      <List dense disablePadding sx={{ mb: 2 }}>
        {limits.map(({ group, time }) => (
          <ListItem key={group} disableGutters sx={{ py: 0.3, display: 'flex', gap: 1 }}>
            <ListItemText
              primary={group}
              secondary={time}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.8rem' }}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
        <strong>Note:</strong> Entries exceeding time limits will be adjudicated but ineligible to
        receive awards. Dance entry choreography may only be performed <strong>once</strong> at DANCEFEST.
      </Typography>
    </>
  );
}

// --- Tab 3: Levels of Performance ---
function LevelsTab() {
  const levels = [
    {
      name: 'Novice (N)',
      desc: 'NEW to performing and competing anywhere. Dancers will perform ONE time at this level for each performance dance style.',
    },
    {
      name: 'Intermediate 1',
      desc: 'Have performed at OSSDF; MORE than 1 year and LESS than 3 years of training with the secondary dance course curriculum; and/or LESS than 2 years experience with other dance competitions.',
    },
    {
      name: 'Intermediate 2',
      desc: 'Have performed at OSSDF; MORE than 1 year and LESS than 3 years of training with the secondary dance course curriculum; and/or MORE than 2 years experience with other dance competitions.',
    },
    {
      name: 'Advanced 1',
      desc: 'Extensive training from a private dance studio and/or MORE than 3 years of competition experience.',
    },
    {
      name: 'Advanced 2',
      desc: 'Extensive training from a private dance studio and/or MORE than 4 years of competition experience.',
    },
  ];
  return (
    <List dense disablePadding>
      {levels.map(({ name, desc }) => (
        <ListItem key={name} disableGutters sx={{ py: 0.75, flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>{name}</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{desc}</Typography>
          <Divider sx={{ mt: 0.75, width: '100%' }} />
        </ListItem>
      ))}
    </List>
  );
}

// --- Tab 4: High-Risk Movements ---
function HighRiskTab() {
  const examples = [
    'Inversions with flight (e.g., aerials, back tucks)',
    'Gymnastic-style tricks (e.g., back handsprings, front walkovers)',
    'Headspins',
    'Lifts involving height or balance above the shoulders',
    'Drops from elevated positions',
    'Partner tricks with throws or unsupported inversions',
    'Movements requiring advanced acro technique beyond standard dance training',
  ];
  const guidance = [
    'Acrobatic elements may be included sparingly and must be integrated smoothly.',
    'Maximum 3 acrobatic movements per routine (OPEN category: max 6).',
    'Level in OPEN category is up to the professional judgment of the teacher supervisor.',
    'All movements must be executed with control, precision, and safety.',
    'High-risk skills must be taught under a qualified dance instructor or coach.',
    'Unsafe or unstable lifts can result in point deductions or disqualification.',
  ];
  const bestPractices = [
    'Include injury prevention training (conditioning, flexibility, warm-ups).',
    'Avoid placing multiple high-risk tricks close together without transition.',
    'Adapt choreography to each dancer\'s ability.',
    'Prioritize clean execution over risky showmanship.',
  ];
  return (
    <>
      <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 1.5 }}>
        Acrobatic movements can enhance performance when executed safely and artistically. In an
        educational setting, the primary objectives are to foster artistic growth, promote safe
        physical development, ensure equity, and emphasize technique over spectacle.
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Examples of High-Risk Movements</Typography>
      <List dense disablePadding sx={{ mb: 1.5 }}>
        {examples.map(e => (
          <ListItem key={e} disableGutters sx={{ py: 0.1, pl: 1 }}>
            <ListItemText primary={`• ${e}`} primaryTypographyProps={{ fontSize: '0.8rem' }} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Guidance for Incorporating Acrobatics</Typography>
      <List dense disablePadding sx={{ mb: 1.5 }}>
        {guidance.map(g => (
          <ListItem key={g} disableGutters sx={{ py: 0.1, pl: 1 }}>
            <ListItemText primary={`• ${g}`} primaryTypographyProps={{ fontSize: '0.8rem' }} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Best Practices for Coaches & Choreographers</Typography>
      <List dense disablePadding>
        {bestPractices.map(b => (
          <ListItem key={b} disableGutters sx={{ py: 0.1, pl: 1 }}>
            <ListItemText primary={`• ${b}`} primaryTypographyProps={{ fontSize: '0.8rem' }} />
          </ListItem>
        ))}
      </List>
    </>
  );
}

// --- Main floating panel ---
function RulesPanel({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [pos, setPos] = useState({ x: Math.max(0, window.innerWidth - PANEL_WIDTH - 40), y: 80 });
  const [size, setSize] = useState({ w: PANEL_WIDTH, h: PANEL_HEIGHT });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizing = useRef(false);
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, w: 0, h: 0 });

  const onDragMouseDown = useCallback((e) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };

    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const x = Math.min(Math.max(0, e.clientX - dragOffset.current.x), window.innerWidth - size.w);
      const y = Math.min(Math.max(0, e.clientY - dragOffset.current.y), window.innerHeight - 60);
      setPos({ x, y });
    };
    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [pos, size]);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, w: size.w, h: size.h };

    const onMouseMove = (e) => {
      if (!resizing.current) return;
      const w = Math.max(MIN_WIDTH, resizeStart.current.w + (e.clientX - resizeStart.current.mouseX));
      const h = Math.max(MIN_HEIGHT, resizeStart.current.h + (e.clientY - resizeStart.current.mouseY));
      setSize({ w, h });
    };
    const onMouseUp = () => {
      resizing.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [size]);

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Title bar / drag handle */}
      <Box
        onMouseDown={onDragMouseDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          flexShrink: 0,
        }}
      >
        <DragIcon sx={{ mr: 1, opacity: 0.8, fontSize: '1.1rem' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: '0.9rem' }}>
          Rules &amp; Regulations
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          onMouseDown={e => e.stopPropagation()}
          sx={{ color: 'primary.contrastText', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
        TabIndicatorProps={{ style: { height: 3 } }}
      >
        <Tab label="Dance Styles" sx={{ fontSize: '0.75rem', minHeight: 36, py: 0.5 }} />
        <Tab label="Group Sizes" sx={{ fontSize: '0.75rem', minHeight: 36, py: 0.5 }} />
        <Tab label="Levels" sx={{ fontSize: '0.75rem', minHeight: 36, py: 0.5 }} />
        <Tab label="High-Risk Movements" sx={{ fontSize: '0.75rem', minHeight: 36, py: 0.5 }} />
      </Tabs>

      {/* Tab content — scrollable */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <TabPanel value={tab} index={0}><DanceStylesTab /></TabPanel>
        <TabPanel value={tab} index={1}><GroupSizesTab /></TabPanel>
        <TabPanel value={tab} index={2}><LevelsTab /></TabPanel>
        <TabPanel value={tab} index={3}><HighRiskTab /></TabPanel>
      </Box>

      {/* Resize handle — bottom-right corner */}
      <Box
        onMouseDown={onResizeMouseDown}
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 18,
          height: 18,
          cursor: 'nwse-resize',
          zIndex: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 3,
            right: 3,
            width: 10,
            height: 10,
            borderRight: '2px solid',
            borderBottom: '2px solid',
            borderColor: 'text.disabled',
            borderRadius: '0 0 2px 0',
          },
        }}
      />
    </Paper>
  );
}

export default RulesPanel;
